import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Target, LayoutDashboard, Trophy, History, LogOut, Settings, BarChart3 } from 'lucide-react';

export default function Layout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/auth';
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img src="/logo.jpg" alt="Los Pistoleros" className="w-10 h-10 rounded-full" />
              <h1 className="font-barlow font-bold text-2xl tracking-tight text-slate-900">LOS PISTOLEROS</h1>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              <Button
                data-testid="nav-dashboard"
                onClick={() => navigate('/dashboard')}
                variant={isActive('/dashboard') ? 'default' : 'ghost'}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  isActive('/dashboard') 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-700 hover:bg-stone-100'
                }`}
              >
                <LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Dashboard
              </Button>
              <Button
                data-testid="nav-standings"
                onClick={() => navigate('/standings')}
                variant={isActive('/standings') ? 'default' : 'ghost'}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  isActive('/standings') 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-700 hover:bg-stone-100'
                }`}
              >
                <Trophy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Standings
              </Button>
              <Button
                data-testid="nav-history"
                onClick={() => navigate('/history')}
                variant={isActive('/history') ? 'default' : 'ghost'}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  isActive('/history') 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-700 hover:bg-stone-100'
                }`}
              >
                <History className="w-4 h-4 mr-2" strokeWidth={1.5} />
                History
              </Button>
              <Button
                data-testid="nav-statistics"
                onClick={() => navigate('/statistics')}
                variant={isActive('/statistics') ? 'default' : 'ghost'}
                className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                  isActive('/statistics') 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-700 hover:bg-stone-100'
                }`}
              >
                <BarChart3 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Stats
              </Button>
              {user?.is_admin && (
                <Button
                  data-testid="nav-admin"
                  onClick={() => navigate('/admin')}
                  variant={isActive('/admin') ? 'default' : 'ghost'}
                  className={`font-barlow font-semibold uppercase tracking-wide text-sm rounded-sm ${
                    isActive('/admin') 
                      ? 'bg-slate-900 text-white' 
                      : 'text-slate-700 hover:bg-stone-100'
                  }`}
                >
                  <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Admin
                </Button>
              )}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <div className="hidden md:block text-right">
                <p className="font-inter font-medium text-sm text-slate-900">{user.name}</p>
                <p className="font-inter text-xs text-stone-600">{user.email}</p>
              </div>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                className="border-stone-300 text-slate-900 hover:bg-stone-100 rounded-sm font-barlow font-semibold uppercase tracking-wide text-sm"
              >
                <LogOut className="w-4 h-4 md:mr-2" strokeWidth={1.5} />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <nav className="md:hidden flex gap-2 mt-4 overflow-x-auto">
            <Button
              onClick={() => navigate('/dashboard')}
              variant={isActive('/dashboard') ? 'default' : 'ghost'}
              className={`font-barlow font-semibold uppercase tracking-wide text-xs rounded-sm flex-shrink-0 ${
                isActive('/dashboard') 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-700 hover:bg-stone-100'
              }`}
            >
              <LayoutDashboard className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Dashboard
            </Button>
            <Button
              onClick={() => navigate('/standings')}
              variant={isActive('/standings') ? 'default' : 'ghost'}
              className={`font-barlow font-semibold uppercase tracking-wide text-xs rounded-sm flex-shrink-0 ${
                isActive('/standings') 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-700 hover:bg-stone-100'
              }`}
            >
              <Trophy className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Standings
            </Button>
            <Button
              onClick={() => navigate('/history')}
              variant={isActive('/history') ? 'default' : 'ghost'}
              className={`font-barlow font-semibold uppercase tracking-wide text-xs rounded-sm flex-shrink-0 ${
                isActive('/history') 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-700 hover:bg-stone-100'
              }`}
            >
              <History className="w-4 h-4 mr-1" strokeWidth={1.5} />
              History
            </Button>
            <Button
              onClick={() => navigate('/statistics')}
              variant={isActive('/statistics') ? 'default' : 'ghost'}
              className={`font-barlow font-semibold uppercase tracking-wide text-xs rounded-sm flex-shrink-0 ${
                isActive('/statistics') 
                  ? 'bg-slate-900 text-white' 
                  : 'text-slate-700 hover:bg-stone-100'
              }`}
            >
              <BarChart3 className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Stats
            </Button>
            {user?.is_admin && (
              <Button
                onClick={() => navigate('/admin')}
                variant={isActive('/admin') ? 'default' : 'ghost'}
                className={`font-barlow font-semibold uppercase tracking-wide text-xs rounded-sm flex-shrink-0 ${
                  isActive('/admin') 
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-700 hover:bg-stone-100'
                }`}
              >
                <Settings className="w-4 h-4 mr-1" strokeWidth={1.5} />
                Admin
              </Button>
            )}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>{children}</main>
    </div>
  );
}